import Constants from '@/Constants';
import { EntityType } from '@/models/EntityTypes';
import { analyticsService, LMSEvents, PlaylistEvents } from '@/services/AnalyticsService';
import { contentService } from '@/services/ContentService';
import { courseService } from '@/services/CourseService';
import { programService } from '@/services/ProgramService';
import { ApplicationState } from '@/store';
import {
    removeContentFavoriteRequest,
    removeCourseFavoriteRequest,
    removeProgramFavoriteRequest,
    setContentFavoriteRequest,
    setCourseFavoriteRequest,
    setProgramFavoriteRequest,
} from '@/store/favorite/actions';
import { Favorite } from '@/store/favorite/types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';

interface Props {
    buttonMode?: boolean;
    entityType: EntityType;
    entityId: number;
    entityName: string;
    moduleId: number;
    customClass: string;
    customActiveClass: string;
    innerCustomClass?: string;
    ownerId?: number;
    description?: string;
    descriptionCustomClass?: string;
}

const logEvent = (entityId: number, entityName: string, entityType: string, moduleId: number): void => {
    let eventName;

    if (entityType === EntityType.CONTENT) {
        eventName = PlaylistEvents.FAVORITE_ITEM;
    } else if (entityType === EntityType.PROGRAM) {
        eventName = LMSEvents.FAVORITE_PROGRAM;
    } else {
        eventName = moduleId === Constants.PLAYLIST_MODULE_ID ? PlaylistEvents.FAVORITE_PLAYLIST : LMSEvents.FAVORITE_COURSE;
    }

    analyticsService.logEvent(eventName, {
        entityId,
        entityName,
        entityType,
    });
};

const FavoriteSection: React.FC<Props> = (props: Props) => {
    const {
        buttonMode,
        customClass,
        customActiveClass,
        entityType,
        entityName,
        entityId,
        moduleId,
        innerCustomClass,
        ownerId,
        description,
        descriptionCustomClass,
    } = props;

    const [favorite, setFavorite] = useState(false);
    const { favoriteList, personId } = useSelector((state: ApplicationState) => ({
        favoriteList: state.favorite.favoriteList,
        personId: state.identity.data.person.id,
    }));

    const isContent: boolean = entityType === EntityType.CONTENT;
    const isProgram: boolean = entityType === EntityType.PROGRAM;
    const service = () => {
        if (isContent) {
            return contentService;
        } else if (isProgram) {
            return programService;
        } else {
            return courseService;
        }
    };

    useEffect(() => {
        setFavorite(service().isFavorited(entityId, favoriteList));
    }, [entityType, entityId, favoriteList]);

    const dispatch: Dispatch = useDispatch();

    const onFavoriteToggleHandler = (event: React.MouseEvent): void => {
        event.preventDefault();
        event.stopPropagation();

        const addOrRemoveFavoriteIndex = () => {
            if (isContent) {
                return 1;
            } else if (isProgram) {
                return 2;
            } else {
                return 0;
            }
        };

        const removeFnc: ((favoriteData: Favorite) => AnyAction)[] = [
            removeCourseFavoriteRequest,
            removeContentFavoriteRequest,
            removeProgramFavoriteRequest,
        ];
        const addFnc: ((favoriteData: Favorite) => AnyAction)[] = [setCourseFavoriteRequest, setContentFavoriteRequest, setProgramFavoriteRequest];
        if (favorite) {
            dispatch(removeFnc[addOrRemoveFavoriteIndex()]({ contentId: entityId } as Favorite));
        } else {
            dispatch(addFnc[addOrRemoveFavoriteIndex()]({ contentId: entityId } as Favorite));
            logEvent(entityId, entityName, entityType, moduleId);
        }

        setFavorite(!favorite);
    };

    if ((entityType === EntityType.COURSE || EntityType.PROGRAM) && ownerId === personId) {
        return null;
    }

    if (buttonMode) {
        return (
            <button className={`btn btn-link ${customClass} ${favorite && customActiveClass}`} onClick={onFavoriteToggleHandler}>
                <i className="material-icons outline">{Constants.icons.FAVORITE_BORDER}</i>
                <i className="material-icons fill">{Constants.icons.FAVORITE}</i>
            </button>
        );
    }

    return (
        <a className={`${customClass} ${favorite && customActiveClass}`} href="" onClick={onFavoriteToggleHandler}>
            <i className={`material-icons ${innerCustomClass} ${favorite && customActiveClass}`}>
                {favorite ? Constants.icons.FAVORITE : Constants.icons.FAVORITE_BORDER}
            </i>
            {description && (
                <span className={`${descriptionCustomClass ? descriptionCustomClass : ''} ${favorite && customActiveClass}`}>{description}</span>
            )}
        </a>
    );
};

export default FavoriteSection;
